<template>
  <div>
    <p class="mb-lg">
      <template v-if="templatesWithRoles.length">
        {{ $tc('bulkSend.steps.roles.description.templates', templates.length) }}
        {{ $t('bulkSend.steps.roles.description.roles') }}
        <template v-if="templatesWithCopyOnlyRoles.length">
          {{ $tc('bulkSend.steps.roles.description.copyOnly', templates.length)  }}
        </template>
        <template v-else>
          {{ $t('bulkSend.steps.roles.description.doBelow') }}
        </template>
      </template>
      <template v-else>
        {{
          $tc(
            'bulkSend.steps.roles.noRolesDescription.templates',
            templates.length,
          )
        }}
        {{ $t('bulkSend.steps.roles.noRolesDescription.roles') }}
      </template>
    </p>

    <div
      class="gap-base flex flex-col sm:mx-auto sm:grid lg:max-w-screen-2xl"
      :class="{ 'sm:grid-cols-rolesGrid1': templatesWithRoles.length <= 1 }"
    >
      <HeaderContainer
        v-for="(party, index) in parties"
        :key="index"
        class="shadow-box"
      >
        <template v-slot:header>
          <div class="flex items-center justify-between">
            <p class="py-sm m-0 font-bold">
              {{ $t('bulkSend.steps.roles.party') }}
              {{ index + 1 }}
            </p>

            <div class="flex items-center">
              <ToggleInput
                class="toggle text-midGrey ml-base"
                :value="isCopy(party)"
                @input="$emit('toggle', index)"
                :label="$t('bulkSend.steps.roles.copy')"
                :label-first="true"
              />

              <button
                class="px-twenty py-sm ml-base -mr-twenty border-none cursor-pointer background-none"
                :class="{ '!cursor-default opacity-50': !canRemoveParty }"
                type="button"
                :aria-label="$t('bulkSend.steps.roles.removeParty')"
                :disabled="!canRemoveParty"
                @click="$emit('remove', index)"
              >
                <XMarkIcon />
              </button>
            </div>
          </div>
        </template>

        <div
          v-if="templatesWithRoles.length"
          class="p-base gap-base sm:grid-cols-rolesGrid3 flex flex-col sm:grid"
        >
          <HeaderContainer
            v-for="template in templatesWithRoles"
            :key="template.fingerprint"
            class="rounded-md"
            :class="{ 'opacity-25': isCopy(party) }"
          >
            <template v-slot:header>
              <label
                :id="`p-${index}-${template.fingerprint}`"
                class="flex leading-normal py-sm -mx-sm"
              >
                {{ $t('bulkSend.steps.roles.on') }}&nbsp;
                <strong
                  :title="template.title"
                  class="truncate"
                >
                  {{ template.title }}
                </strong>
                &nbsp;
                <span class="whitespace-nowrap">
                  {{ $t('bulkSend.steps.roles.theyAre') }}:
                </span>
              </label>
            </template>

            <div
              class="px-sm m-0"
              role="listbox"
              :aria-labelledby="`p-${index}-${template.fingerprint}`"
            >
              <template v-for="role in template.parties.filter(party => party.role === 'signer')">
                <button
                  v-if="!isCopy(party)"
                  :key="role.id"
                  class="flex items-center justify-between w-full px-twenty py-sm my-sm border-none bg-transparent hover:bg-offWhite font-body font-medium text-base text-current rounded-full cursor-pointer"
                  :class="{
                    '!bg-primaryGradient !text-whiteOverride hover:!bg-primaryHoverDark':
                      hasRole(party, role.id),
                    '!text-disabledGrey !cursor-default !bg-transparent hover:!bg-transparent':
                      !availableRole(party, role.id),
                  }"
                  type="button"
                  role="option"
                  :aria-selected="hasRole(party, role.id)"
                  :disabled="!availableRole(party, role.id)"
                  @click="$emit('select', index, role.id)"
                >
                  {{ role.title }}

                  <XMarkIcon v-if="hasRole(party, role.id)" />

                  <span
                    v-if="!availableRole(party, role.id)"
                    class="contents whitespace-nowrap"
                  >
                    ({{ $t('bulkSend.steps.roles.assignedTo', { id: assignedTo(role.id) + 1 }) }})
                  </span>
                </button>

                <button
                  v-else
                  :key="role.id"
                  class="flex items-center justify-between w-full px-twenty py-sm border-none background-none font-body font-medium text-base text-current rounded-full"
                  type="button"
                  :aria-selected="false"
                  disabled
                >
                  {{ role.title }}
                </button>
              </template>
            </div>
          </HeaderContainer>

          <HeaderContainer
            v-for="template in templatesWithoutRoles"
            :key="template.fingerprint"
            class="rounded-md"
            :class="{
              'opacity-25': isCopy(party),
            }"
          >
            <template v-slot:header>
              <label
                :id="`p-${index}-${template.fingerprint}`"
                class="flex leading-normal py-sm -mx-sm"
              >
                {{ $t('bulkSend.steps.roles.on') }}&nbsp;
                <strong
                  :title="template.title"
                  class="truncate"
                >
                  {{ template.title }}
                </strong>
              </label>
            </template>
            <p class="p-twenty italic">
              {{ $t('bulkSend.steps.roles.templateNoRoles') }}
            </p>
          </HeaderContainer>
        </div>

        <p
          v-else
          class="p-twenty text-disabledGrey"
        >
          {{ $t('bulkSend.steps.roles.noRoles') }}
        </p>
      </HeaderContainer>
    </div>

    <IconButton
      class="mt-lg"
      size="small"
      variant="secondary"
      icon="plus"
      @click="$emit('add')"
    >
      {{ $t('bulkSend.steps.roles.addParty') }}
    </IconButton>

    <p class="flex justify-end mt-base">
      <template v-if="unassignedRoles">
        <span
          class="inline-flex justify-center items-center text-white bg-primaryGradient h-twentyFive w-twentyFive mr-sm rounded-full flex-shrink-0"
          :class="{ '!w-auto px-sm': unassignedRoles > 9 }"
        >
          {{ unassignedRoles }}
        </span>
        {{ $t('bulkSend.steps.roles.remaining') }}
      </template>
      <template v-else>
        <CheckIcon class="text-white bg-success3 rounded-full h-base w-base p-xs mr-sm flex-shrink-0" />
        {{ $t('bulkSend.steps.roles.complete') }}
      </template>
    </p>
  </div>
</template>

<script>
// @ts-check
import { defineComponent, toRefs } from 'vue';

import { useBulkSendRolesStatus } from '@/composables/bulkSend/roles';

import IconButton from '@/components/library/buttons/IconButton.vue';
import ToggleInput from '@/components/tailwind/forms/ToggleInput.vue';
import { HeaderContainer } from '@/components/library/containers/HeaderContainer';
import { CheckIcon, XMarkIcon } from '@/components/icons/fontIcons';

/** @typedef {import('@/store/modules/bulkSend/models/Parties').TUpdateParties} TUpdateParties */
/** @typedef {import('@/store/modules/bulkSend/models/Templates').TTemplates} TTemplates */

export default defineComponent({
  name: 'RolesSelector',
  components: {
    HeaderContainer,
    ToggleInput,
    IconButton,
    CheckIcon,
    XMarkIcon,
  },
  props: {
    /** @type {import('vue').PropOptions<TUpdateParties>} */
    parties: {
      type: Array,
      required: true,
    },
    /** @type {import('vue').PropOptions<TTemplates>} */
    templates: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const { parties, templates } = toRefs(props);

    return useBulkSendRolesStatus(parties, templates);
  },
});
</script>

<style scoped>
.background-none {
  background: none;
}

/* customise toggle for bulk send */
.toggle :deep(.label) {
  @apply text-midGray text-12 mt-0 mr-base;
}
.toggle :deep(.block) {
  @apply h-5 bg-whiteOverride border-1 border-solid border-border;
  width: 2.5rem;
}
.toggle :deep(.dot) {
  @apply top-0 left-0 bg-whiteOverride border-border;
}
.toggle :deep(input:checked ~ .block) {
  @apply bg-signableGreen;
}
.toggle :deep(input:checked ~ .dot) {
  @apply border-border;
}
</style>
